import React, { useEffect } from 'react';
import { bool, func, string } from 'prop-types';
import { connect } from 'react-redux';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { pathByRouteName } from '../../util/routes';
import { apiBaseUrl } from '../../util/api';
import { authInfo } from '../../ducks/auth.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { flushPendingSearches } from '../../util/pendingSearches';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

function loadGsiScript() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      resolve();
      return;
    }
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    if (existing) {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('gsi-load-failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('gsi-load-failed'));
    document.head.appendChild(s);
  });
}

/**
 * Google Identity Services (One Tap). Misma sesión Sharetribe que el botón OAuth.
 */
const GoogleOneTap = props => {
  const { enabled, from, authInProgress, isAuthenticated, onAuthRefresh } = props;
  const routeConfiguration = useRouteConfiguration();
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (!enabled || !clientId || authInProgress || isAuthenticated) {
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      try {
        await loadGsiScript();
        if (cancelled || typeof window === 'undefined' || !window.google?.accounts?.id) {
          return;
        }

        const defaultReturn = pathByRouteName('LandingPage', routeConfiguration) || '/';
        const defaultConfirm = pathByRouteName('ConfirmPage', routeConfiguration) || '/confirm';

        const handleCredential = async response => {
          if (!response?.credential || cancelled) {
            return;
          }
          try {
            const res = await fetch(`${apiBaseUrl()}/api/auth/google-one-tap`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                credential: response.credential,
                ...(from ? { from } : {}),
                defaultReturn,
                defaultConfirm,
              }),
            });
            const data = await res.json().catch(() => ({}));
            // Si el backend creó sesión (cookie), refrescamos Redux para que la UI (Topbar, etc.)
            // se actualice sin depender de un redirect.
            if (data?.success) {
              await onAuthRefresh();
            }
            if (data?.redirect) {
              window.location.assign(data.redirect);
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        };

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredential,
          use_fedcm_for_prompt: true,
          auto_select: true,
        });

        window.google.accounts.id.prompt();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };

    run();

    return () => {
      cancelled = true;
      try {
        window.google?.accounts?.id?.cancel();
      } catch (e) {
        // ignore
      }
    };
  }, [enabled, clientId, from, authInProgress, isAuthenticated, routeConfiguration]);

  return null;
};

GoogleOneTap.propTypes = {
  enabled: bool.isRequired,
  authInProgress: bool.isRequired,
  isAuthenticated: bool.isRequired,
  onAuthRefresh: func.isRequired,
  from: string,
};

GoogleOneTap.defaultProps = {
  from: null,
};

const mapStateToProps = state => ({
  isAuthenticated: state.auth.isAuthenticated,
});

const mapDispatchToProps = dispatch => ({
  // One Tap can log in without a redirect, so it never reaches the boot flush in index.js.
  onAuthRefresh: () =>
    dispatch(authInfo())
      .then(() => dispatch(fetchCurrentUser()))
      .then(() => flushPendingSearches()),
});

export default connect(mapStateToProps, mapDispatchToProps)(GoogleOneTap);
